import { useCallback, useEffect, useReducer } from 'react';
import { set } from 'lodash-es';
import { newId } from '#helpers/helpers.js';

const ACTIONS = {
    ADD_USER: 'add_user',
    CLONE_USER: 'clone_user',
    REMOVE_USER: 'remove_user',
    EDIT_USER: 'edit_user',
    REPLACE_STATE: 'replace_state',
    RESET_USER: 'reset_user',
    SELECT_USER: 'select_user'
};

export const makeEmptyUser = () => ({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    imageProfile: null,
    enabled: false,
    isEditing: false,
    isNew: true
});

function reducer(state, action) {
    const sortingFn = (a, b) => {
        if (a.main && !b.main) return -1;
        if (!a.main && b.main) return 1;

        if (a.isNew && !b.isNew) return 1;
        if (!a.isNew && b.isNew) return -1;

        const aFullName = `${a.firstName} ${a.lastName}`.toLowerCase();
        const bFullName = `${b.firstName} ${b.lastName}`.toLowerCase();

        if (aFullName < bFullName) return -1;
        if (aFullName > bFullName) return 1;

        return a.email?.toLowerCase() <= b.email?.toLowerCase() ? -1 : 1;
    };

    switch (action.type) {
        case ACTIONS.REPLACE_STATE: {
            let { users } = action.payload;
            return users.sort(sortingFn);
        }
        case ACTIONS.RESET_USER: {
            const { dbModel, id } = action.payload;
            const updated = structuredClone(state);
            let index = updated.findIndex((el) => el.id === id);
            updated[index] = dbModel;
            return updated.sort(sortingFn);
        }
        case ACTIONS.ADD_USER: {
            const updated = structuredClone(state);
            const newUser = makeEmptyUser();
            newUser.id = newId();
            newUser.isEditing = true;
            updated.push(newUser);
            return updated;
        }
        case ACTIONS.REMOVE_USER: {
            const { id } = action.payload;
            const updated = structuredClone(state);
            const newState = updated.filter((el) => el.id !== id);
            return newState;
        }
        case ACTIONS.EDIT_USER: {
            const { id, field, data } = action.payload;
            const updated = structuredClone(state);
            const user = updated.find((el) => el.id === id);
            user.isEditing = true;
            set(user, field, data);
            return updated;
        }
    }
}

export function useUsers(initialState, selectedUserId, setSelectedUserId, init) {
    const [state, dispatch] = useReducer(reducer, initialState, init); // lista di utenti

    // returns true if the state need to be updated
    const handleIncomingState = useCallback(
        (initialState, selectedUserId, setSelectedUserId) => {
            if (!initialState) return false;
            if (Array.isArray(state)) {
                let outdated = state.find((el) => el.isNew);
                // se creo un nuovo utente aggiorno l'id selezionato
                if (outdated) {
                    // se ho fatto una ricerca c'è ancora l'id con NEW e updated è undefined
                    const updated = initialState.find((el) => el.email === outdated.email);
                    if (!updated) initialState.push(outdated);
                    // setto con l'id
                    setSelectedUserId(updated ? updated.id : outdated.id);
                } else {
                    // se filtro i risultati mantengo il modello dell'utente selezionato
                    outdated = state.find((el) => el.id === selectedUserId);
                    const newIndex = initialState.findIndex((el) => el.id === selectedUserId);
                    if (outdated && newIndex >= 0) initialState[newIndex] = outdated;
                    else outdated && initialState.push(outdated);
                }
            }
            return true;
        },
        [state]
    );

    useEffect(() => {
        const needUpdate = handleIncomingState(initialState, selectedUserId, setSelectedUserId);
        if (needUpdate)
            dispatch({
                type: ACTIONS.REPLACE_STATE,
                payload: {
                    users: initialState
                }
            });
    }, [initialState]);

    const addUser = useCallback(() => {
        dispatch({
            type: ACTIONS.ADD_USER
        });
    }, []);

    const removeUser = useCallback((id) => {
        dispatch({
            type: ACTIONS.REMOVE_USER,
            payload: { id }
        });
    }, []);

    const editUser = useCallback((id, field, data) => {
        dispatch({
            type: ACTIONS.EDIT_USER,
            payload: { id, field, data }
        });
    }, []);

    const resetUser = useCallback((id, dbModel) => {
        dispatch({
            type: ACTIONS.RESET_USER,
            payload: { id, dbModel }
        });
    }, []);

    return {
        users: state,
        addUser,
        removeUser,
        editUser,
        resetUser
    };
}
